import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { ProductGroup } from "../domain/productGroup";
import { useProductGroupChanged } from "../composables/productGroupChanged";

export const useProductGroupStore = defineStore('product-groups', () => {
  const groups = ref<ProductGroup[]>([]);
  const changed = useProductGroupChanged();
  const sortedByName = computed(() => {
    const clone = Array.from(groups.value);
    clone.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    return clone;
  });

  function add(group: ProductGroup) {
    removeById(group.id);
    groups.value.push(group);
    changed.emit({
      id: group.id,
      action: 'updated',
      data: group
    });
  }

  function remove(id: string) {
    if (removeById(id)) {
      changed.emit({
        id: id,
        action: 'removed'
      });
    }
  }

  function getById(id: string|null) {
    for (const group of groups.value) {
      if (group.id === id) {
        return group;
      }
    }

    return null;
  }

  function merge(models: ProductGroup[]) {
    const ids = new Set<string>();

    for (const group of models) {
      add(group);
      ids.add(group.id);
    }

    for (const group of groups.value) {
      if (!ids.has(group.id)) {
        remove(group.id);
      }
    }
  }

  function removeById(id: string) {
    const index = groups.value.findIndex(p => p.id === id);
    if (index > -1) {
      groups.value.splice(index, 1);
      return true;
    }

    return false;
  }

  function clear() {
    const ids = groups.value.map(p => p.id);
    groups.value.length = 0;

    for (const id of ids) {
      changed.emit({
        id: id,
        action: 'removed'
      });
    }

    return ids;
  }

  return {
    groups,
    sortedByName,
    add,
    remove,
    merge,
    clear,
    getById
  };

});
